// WebAudio call-progress tones — no assets, silently do nothing where
// AudioContext is unavailable (visual state still shows; tones are
// best-effort and must never affect the call).
//
// - reconnect: soft beep every ~2 s for the "Reconnecting…" state
//   (docs/06 §5 table, §3 "reconnecting audio cue")
// - ringback: what the CALLER hears while the remote side rings
//   (single low tone, telephony-style cadence)
// - ringtone: incoming-call ring for the WEB app only — Android plays the
//   system ringtone natively (CallNotificationHelper) and must not get a
//   second one from the WebView.

export interface TonePlayer {
  start(): void;
  stop(): void;
}

/** One oscillator burst inside a repeating pattern. */
export interface Note {
  atS: number; // offset from the start of each repetition
  durS: number;
  freqHz: number;
  gain: number;
  /** Oscillator shape; 'square'/'triangle' give the chiptune character.
   *  Defaults to 'sine' (the classic call tones). */
  wave?: OscillatorType;
}

export interface TonePattern {
  intervalMs: number;
  notes: Note[];
}

export type AudioContextCtor = new () => AudioContext;

export const defaultCtor = (): AudioContextCtor | undefined =>
  (globalThis as { AudioContext?: AudioContextCtor }).AudioContext;

export function createPatternTone(
  pattern: TonePattern,
  getCtor: () => AudioContextCtor | undefined = defaultCtor,
): TonePlayer {
  let ctx: AudioContext | null = null;
  let interval: ReturnType<typeof setInterval> | null = null;

  function playOnce(): void {
    if (!ctx) return;
    try {
      for (const note of pattern.notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = note.wave ?? 'sine';
        osc.frequency.value = note.freqHz;
        // A short attack/release envelope: square/triangle notes click badly
        // without one, and the ramp is what makes the arpeggios sound "played".
        const t0 = ctx.currentTime + note.atS;
        const t1 = t0 + note.durS;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(note.gain, t0 + Math.min(0.01, note.durS / 2));
        gain.gain.setValueAtTime(note.gain, Math.max(t0, t1 - 0.02));
        gain.gain.linearRampToValueAtTime(0, t1);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t0);
        osc.stop(t1);
      }
    } catch {
      // tone is best-effort; never let audio failures affect the call
    }
  }

  return {
    start(): void {
      if (interval !== null) return;
      const Ctor = getCtor();
      if (!Ctor) return;
      try {
        ctx ??= new Ctor();
        // Autoplay policies leave a gesture-less context suspended; resuming
        // is best-effort (a prior tap anywhere in the app usually suffices).
        if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
      } catch {
        return;
      }
      playOnce();
      interval = setInterval(playOnce, pattern.intervalMs);
    },
    stop(): void {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
      void ctx?.close().catch(() => undefined);
      ctx = null;
    },
  };
}

/** Soft short beep every ~2 s: quiet reassurance during ICE reconnects. */
export function createReconnectTone(
  getCtor: () => AudioContextCtor | undefined = defaultCtor,
): TonePlayer {
  return createPatternTone(
    { intervalMs: 2_000, notes: [{ atS: 0, durS: 0.15, freqHz: 440, gain: 0.08 }] },
    getCtor,
  );
}

/** Caller-side ringback: 1 s of 425 Hz, then 3 s of silence (telephony cadence). */
export function createRingbackTone(
  getCtor: () => AudioContextCtor | undefined = defaultCtor,
): TonePlayer {
  return createPatternTone(
    { intervalMs: 4_000, notes: [{ atS: 0, durS: 1.0, freqHz: 425, gain: 0.06 }] },
    getCtor,
  );
}

/** Web incoming ring: a brighter two-note chime, repeating every ~2.5 s. */
export function createRingtone(
  getCtor: () => AudioContextCtor | undefined = defaultCtor,
): TonePlayer {
  return createPatternTone(
    {
      intervalMs: 2_500,
      notes: [
        { atS: 0, durS: 0.3, freqHz: 740, gain: 0.15 },
        { atS: 0.45, durS: 0.3, freqHz: 880, gain: 0.15 },
      ],
    },
    getCtor,
  );
}
